import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const SHARED_COORDINATOR_URL = (import.meta.env.VITE_SHARED_COORDINATOR_URL || 'https://next-events-engine.will-026.workers.dev').replace(/\/*$/, '');

type CoordinatorConfig = {
  protocolVersion: string;
  cloudRequired: boolean;
  originalMediaUploadRequired: boolean;
  clientApiBackend?: string;
  pairing?: {
    browserHandoff: boolean;
    qrCode: boolean;
    manualCode: boolean;
    localOnlyAllowed: boolean;
  };
  supportedClientTypes?: string[];
  supportedAppProducts?: string[];
};

type ApiResponse = {
  ok: boolean;
  data?: CoordinatorConfig;
  error?: string;
};

function formatBoolean(value: boolean | undefined) {
  return value ? 'Yes' : 'No';
}

export function SetupPage() {
  const [config, setConfig] = useState<CoordinatorConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const response = await fetch(`${SHARED_COORDINATOR_URL}/api/client/config`, { cache: 'no-store' });
        const json = (await response.json().catch(() => null)) as ApiResponse | null;
        if (!response.ok || !json?.ok || !json.data) {
          throw new Error(json?.error || `Could not load shared coordinator config (${response.status}).`);
        }
        if (alive) {
          setConfig(json.data);
        }
      } catch (err) {
        if (alive) {
          setError(err instanceof Error ? err.message : 'Could not load setup status.');
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#090b0f] text-[#edf2f7] px-4 py-10">
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <p className="text-xs uppercase tracking-[0.12em] text-[#62d3b5]">Shared Cloud Setup</p>
          <h1 className="mt-1 text-2xl font-semibold">Setup &amp; account flow</h1>
          <p className="mt-2 max-w-3xl text-sm text-[#9fb0c3]">
            This page explains how to get Event.Tools metadata browsing in a browser without storing media files in the web app.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/account" className="rounded bg-[#1b2938] px-3 py-2 text-sm text-[#dbe6f2] hover:bg-[#273a4f]">
              Account
            </Link>
            <Link to="/pair" className="rounded bg-[#1b2938] px-3 py-2 text-sm text-[#dbe6f2] hover:bg-[#273a4f]">
              Generate pairing code
            </Link>
            <Link to="/library" className="rounded bg-[#1b2938] px-3 py-2 text-sm text-[#dbe6f2] hover:bg-[#273a4f]">
              Browse library
            </Link>
          </div>
        </header>

        <section className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium text-[#d9e2ec]">Coordinator status</h2>
          {loading ? (
            <p className="mt-2 text-sm text-[#9fb0c3]">Checking shared backend status...</p>
          ) : error ? (
            <p className="mt-2 text-sm text-[#ff6b6b]">{error}</p>
          ) : config ? (
            <ul className="mt-2 space-y-1.5 text-sm">
              <li><span className="text-[#9fb0c3]">Protocol:</span> <span className="font-mono text-[#d9e2ec]">{config.protocolVersion}</span></li>
              <li><span className="text-[#9fb0c3]">Cloud required:</span> {formatBoolean(!config.cloudRequired)}</li>
              <li><span className="text-[#9fb0c3]">Original media upload required:</span> {formatBoolean(!config.originalMediaUploadRequired)}</li>
              <li><span className="text-[#9fb0c3]">Browser handoff:</span> {formatBoolean(config.pairing?.browserHandoff)}</li>
              <li><span className="text-[#9fb0c3]">Manual code pairing:</span> {formatBoolean(config.pairing?.manualCode)}</li>
              <li><span className="text-[#9fb0c3]">Local-only allowed:</span> {formatBoolean(config.pairing?.localOnlyAllowed)}</li>
            </ul>
          ) : null}
        </section>

        <section className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium text-[#d9e2ec]">Workflow</h2>
          <ol className="mt-2 list-decimal pl-5 text-sm text-[#c7d4e1] space-y-2">
            <li>Create or sign in to your account at <Link to="/account" className="text-[#62d3b5] underline">/account</Link>.</li>
            <li>Generate a pairing code at <Link to="/pair" className="text-[#62d3b5] underline">/pair</Link>.</li>
            <li>On Event.Tools desktop, open Shared cloud settings and pair using your account and pairing code.</li>
            <li>Once paired, report project/folder metadata from desktop to this coordinator.</li>
            <li>Open <Link to="/library" className="text-[#62d3b5] underline">/library</Link> from any browser.</li>
          </ol>
        </section>

        <section className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium text-[#d9e2ec]">Offline behavior</h2>
          <ul className="mt-2 list-disc pl-5 text-sm text-[#9fb0c3] space-y-1">
            <li>When no desktop is online, metadata remains browsable and thumbnails already synced stay visible.</li>
            <li>Change and preview actions are disabled until a paired desktop with browser API heartbeat is available.</li>
            <li>No original media files are uploaded to this website.</li>
          </ul>
        </section>

        <section className="rounded border border-[#223042] bg-[#0d1117] p-5">
          <h2 className="text-sm font-medium text-[#d9e2ec]">Support checklist</h2>
          <p className="mt-2 text-sm text-[#9fb0c3]">
            The browser library is intended to mirror and coordinate with the desktop. If this page says the
            coordinator is healthy, pair at least one active Event.Tools desktop and verify a successful heartbeat.
          </p>
          <ul className="mt-3 list-disc pl-5 text-sm text-[#9fb0c3] space-y-1">
            <li>Expected paired org: your workspace that owns the library data.</li>
            <li>Expected synced records: VJ and Event.Tools metadata with counts in /library.</li>
            <li>Expected behavior in web browser: preview requests route to online paired desktop.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default SetupPage;
